import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CONSULT_ACTIVE_STATUS } from 'assets';
import ConsultsSVG from 'assets/images/consults.svg';

import { Icon, Typography } from '@repo/ui';
import { capitalize } from 'helpers';
import useConsultAgents from 'services/general/consultAgentsList';
import { getConsultCreditsCount } from 'store/account';

import { Consult } from 'types/Consult';

import {
  ContainerSizes,
  PageContainer
} from 'components';
import HeaderIllustration from 'components/Header/HeaderIllustration';
import TwoColumnGrid from 'components/TwoColumnGrid/TwoColumnGrid';
import useConsults from 'services/general/consultsList';
import { BookInAdvance } from './BookInAdvance/BookInAdvance';
import { ConsultCard } from './ConsultCard/ConsultCard';
import { ConsultInfo } from './ConsultsInfo/ConsultsInfoPage';

import ConsultBookingCard from './ConsultBookingCard/ConsultBookingCard';
import styles from './Consults.module.scss';

export const getAgentNameFromEmail = (email?: string) => {
  const AGENT_NAMES = ['amy', 'jennie', 'danielle', 'taylor'];
  const name = email?.split('@')[0];
  return !email || !name || !AGENT_NAMES.includes(name)
    ? 'our team'
    : capitalize(name);
};

export const sortRecentToOldestConsult = (a: Consult, b: Consult) =>
  a.scheduled_dt > b.scheduled_dt ? -1 : 1;

const Consults = () => {
  const { data: consultAgentsList } = useConsultAgents();
  const { data: consultsList } = useConsults();

  const consultCreditsCount = useSelector(getConsultCreditsCount);

  const upcomingConsults = useMemo(
    () =>
      consultsList
        ?.filter(consult => CONSULT_ACTIVE_STATUS.includes(consult.status))
        .sort(sortRecentToOldestConsult),
    [consultsList],
  );
  const pastConsults = useMemo(
    () =>
      consultsList
        ?.filter(
          consult =>
            !CONSULT_ACTIVE_STATUS.includes(consult.status) &&
            consult.status !== 'cancelled',
        )
        .sort(sortRecentToOldestConsult),
    [consultsList],
  );
  const cancelledConsults = useMemo(
    () =>
      consultsList
        ?.filter(consult => consult.status === 'cancelled')
        .sort(sortRecentToOldestConsult),
    [consultsList],
  );

  return (
    <>
      {!consultsList?.length && (
        <HeaderIllustration imgSrc={ConsultsSVG} bgColor='#CDEAFA' />
      )}
      <PageContainer
        size={ContainerSizes.XL}
        className={styles.consultsContainer}
      >
        {!!consultsList?.length && (
          <div className='flex flex-column gap-2 mb-4'>
            <Typography variant={'heading-xl'}>Coaching</Typography>
            {!!consultCreditsCount && (
              <div className={styles.iconRow}>
                <Icon name='messageHearthSquare' size='s' />
                <Typography variant={'label'}>
                  {`You have ${consultCreditsCount} free review${
                    consultCreditsCount > 1 ? 's' : ''
                  } remaining.`}
                </Typography>
              </div>
            )}
          </div>
        )}
        <TwoColumnGrid>
          <TwoColumnGrid.Wide mobileOrder={2}>
            <main className={styles.mainContainer}>
              {!consultsList?.length ? (
                <ConsultInfo consultAgentsList={consultAgentsList} />
              ) : (
                <div className='flex flex-column gap-4 mb-4'>
                  {!!upcomingConsults?.length && (
                    <div className='flex flex-column gap-3'>
                      <Typography variant='heading-m'>Upcoming</Typography>
                      <div className='flex flex-column gap-3'>
                        {upcomingConsults.map(consult => (
                          <ConsultCard
                            consult={consult}
                            key={consult.id + '_' + consult.calendly_id}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  <BookInAdvance upcomingConsults={upcomingConsults} />
                  {(!!pastConsults?.length || !!cancelledConsults?.length) && (
                    <div className='flex flex-column gap-3'>
                      <Typography variant='heading-m'>Previous</Typography>
                      <div className='flex flex-column gap-3'>
                        {pastConsults.map(consult => (
                          <ConsultCard consult={consult} key={consult.id} />
                        ))}
                        {cancelledConsults.map(consult => (
                          <ConsultCard consult={consult} key={consult.id} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </main>
          </TwoColumnGrid.Wide>
          <TwoColumnGrid.Slim isSticky mobileOrder={1}>
            <ConsultBookingCard />
          </TwoColumnGrid.Slim>
        </TwoColumnGrid>
      </PageContainer>
    </>
  );
};

export default Consults;
